import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CheckCircleOutlineRounded } from '@mui/icons-material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import { green } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import { selectMaterials } from '@/app/services/materials'
import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useGetOrderQuery } from '@/app/services/orders'
import { useGetStoreSettingsQuery } from '@/app/services/organisation'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import OrderConfirmationToolbar from '@/common/components/AppBar/OrderConfirmationToolbar'
import TbxShadowScroll from '@/common/components/TbxShadowScroll'
import { getAddressDetails } from '@/common/helpers/addressUtilities'
import { formatCurrency, formatNumber } from '@/common/helpers/formatUtilities'
import { useOrderNavigation } from '@/common/hooks/useOrderNavigation'
import { fractionToPercentage, getLargestDimensions, ORDER_PAYMENT_STATUS, ORDER_STATUS_ARRAY } from '@/common/utils'

const classes = {
    container: {
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: (theme) => `1px ${theme.palette.grey[300]} solid`,
        width: '100%',
    },
    successMessageContainer: {
        gap: 3,
        maxWidth: '500px',
        textAlign: 'center',
        marginBottom: 6,
    },
    needHelp: {
        maxWidth: '500px',
    },

    orderSummary: (theme) => ({
        padding: 3,
        gap: 3,
        overflow: 'hidden',
        [theme.breakpoints.up('lg')]: {
            height: 'calc(100vh - 114px)',
        },
    }),
    summaryTitle: {
        fontWeight: 700,
        textTransform: 'uppercase',
    },
    orderInfo: {
        gap: 1.5,
    },
    partsListContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    partsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    partInfo: {
        gap: 0.75,
    },
    partDetailsInfo: {
        gap: 1.5,
    },
    totalInfo: {
        gap: 1.5,
    },
    smallText: {
        fontSize: '0.75rem',
    },
    strongText: {
        fontWeight: 700,
    },
}

const PaymentSuccess = () => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const navigation = useOrderNavigation('paymentSuccess')

    const organisationId = useSelector(selectOrganisationId)

    const {
        data: order,
        isLoading,
        isSuccess,
    } = useGetOrderQuery({
        organisationId,
        orderId,
    })
    const { data: orderItems, isLoading: isLoadingItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const { data: storeSettings } = useGetStoreSettingsQuery({
        organisationId,
    })

    const materials = useSelector((state) => selectMaterials(state, { organisationId }))

    const orderItemMaterial = (orderItem) => materials?.[orderItem.materialId]

    const lengthUnit = storeSettings?.defaultDrawingUnits === 'Metric' ? 'mm' : 'in'
    const weightUnit = storeSettings?.defaultDrawingUnits === 'Metric' ? 'kg' : 'lb'

    const largestDimensions = getLargestDimensions(orderItems, storeSettings)

    const totalWithoutTax = order?.linePrice

    const orderMinimumChargePrice = order?.minimumQuoteChargePrice ?? 0

    const deliveryPrice = order?.deliveryPrice ?? 0

    const subTotalWithDiff = totalWithoutTax + orderMinimumChargePrice

    const total = order?.lineTaxedPrice

    const totalTax = total - subTotalWithDiff

    const orderTaxRate = order?.taxRateId ? order?.taxRate : totalTax / order?.linePrice

    const isOrdered = useMemo(() => {
        return [
            ORDER_STATUS_ARRAY.PendingOrderConfirmation,
            ORDER_STATUS_ARRAY.Ordered,
            ORDER_STATUS_ARRAY.Invoiced,
        ].includes(order?.status)
    }, [order?.status])

    const isOrderPaid = useMemo(() => {
        return (
            order?.paymentStatus === ORDER_PAYMENT_STATUS.GatewayPaid ||
            order?.paymentStatus === ORDER_PAYMENT_STATUS.ManualPaid
        )
    }, [order?.paymentStatus])

    const pickupAddress = storeSettings?.pickupLine1
        ? {
              line1: storeSettings?.pickupLine1,
              line2: storeSettings?.pickupLine2,
              city: storeSettings?.pickupCity,
              state: storeSettings?.pickupState,
              postcode: storeSettings?.pickupPostcode,
              country: storeSettings?.pickupCountry,
          }
        : storeSettings?.pickupAddress

    if (navigation) return navigation

    if (!isLoading && isSuccess && isOrdered && isOrderPaid) {
        return (
            <>
                <OrderConfirmationToolbar />
                <Grid
                    component="section"
                    size={{
                        lg: 7,
                        xs: 12,
                    }}
                    sx={{ alignSelf: 'stretch' }}
                    container
                >
                    <Box
                        sx={[
                            {
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                                p: 6,
                            },
                            classes.container,
                        ]}
                    >
                        <Box
                            sx={[
                                {
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                },
                                classes.successMessageContainer,
                            ]}
                        >
                            <CheckCircleOutlineRounded style={{ fontSize: '88px', color: green[600] }} />
                            <Typography variant="h5">{t('Thank you for your payment')}</Typography>
                            <Typography variant="body1">
                                {t('Hi {{contact}}, a receipt has been emailed to your supplied email address.', {
                                    contact: order?.customerContact?.name,
                                })}
                            </Typography>
                            <Typography variant="h6">
                                {t('Order number: {{orderName}}', { orderName: order?.name })}
                            </Typography>

                            <Typography variant="body1">
                                {t(
                                    'If we have any questions about your order, we will contact you on your contact number or email address provided.'
                                )}
                            </Typography>
                            {order?.deliveryOption === 0 ? (
                                <Typography variant="body1">
                                    {t('When the order is ready, pickup is from {{pickupAddress}}', {
                                        pickupAddress: getAddressDetails(pickupAddress),
                                    })}
                                </Typography>
                            ) : null}

                            {order?.deliveryOption === 1 && order?.deliveryPricingMethod === 'SeparateCharge' ? (
                                <Typography variant="body1">
                                    {t('We will calculate the delivery charge and contact you with a order.')}
                                </Typography>
                            ) : null}

                            {order?.deliveryOption === 1 && order?.deliveryPricingMethod === 'PayUponDelivery' ? (
                                <Typography variant="body1">
                                    {t(
                                        'When the order is ready, {{deliveryProvider}} will deliver the order to your nominated address: {{deliveryAddress}}. A delivery fee is payable directly to the delivery provider.',
                                        {
                                            deliveryProvider: order?.customerDeliveryProvider
                                                ? order?.customerDeliveryProvider
                                                : order?.deliveryProvider,
                                            deliveryAddress: getAddressDetails(order?.deliveryAddress),
                                        }
                                    )}
                                </Typography>
                            ) : null}

                            {order?.deliveryOption === 1 && order?.deliveryPricingMethod === 'ChargeToOrder' ? (
                                <Typography variant="body1">
                                    {t(
                                        'When the order is ready, we will deliver the order to your nominated address: {{deliveryAddress}}.',
                                        {
                                            deliveryAddress: getAddressDetails(order?.deliveryAddress),
                                        }
                                    )}
                                </Typography>
                            ) : null}
                        </Box>

                        {storeSettings?.webStoreSupportEmail ? (
                            <Box sx={classes.needHelp}>
                                <Alert
                                    severity="info"
                                    variant="standard"
                                >
                                    <AlertTitle>{t('Have any questions?')}</AlertTitle>
                                    {t('Please contact us at')}{' '}
                                    <a href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </a>{' '}
                                    {t('from the email address supplied and your order number.')}
                                </Alert>
                            </Box>
                        ) : null}
                    </Box>
                </Grid>
                <Grid
                    component="aside"
                    size={{
                        lg: 5,
                        xs: 12,
                    }}
                    container
                >
                    <Box sx={classes.container}>
                        <Box
                            sx={[
                                {
                                    display: 'flex',
                                    flexDirection: 'column',
                                },
                                classes.orderSummary,
                            ]}
                        >
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    sx={classes.summaryTitle}
                                    variant="body1"
                                >
                                    {t('Order summary')}
                                </Typography>
                                <Typography
                                    sx={classes.summaryTitle}
                                    variant="body1"
                                >
                                    {order.name}
                                </Typography>
                            </Box>

                            <Box
                                sx={[
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                    classes.orderInfo,
                                ]}
                            >
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2">{t('Net weight')}</Typography>
                                    <Typography
                                        className="price"
                                        variant="body2"
                                    >
                                        {formatNumber(order.totalMass, storeSettings?.locale)} {weightUnit}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2">{t('Largest long-dimension')}</Typography>
                                    <Typography
                                        className="price"
                                        variant="body2"
                                    >
                                        {largestDimensions.long}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2">{t('Largest short-dimension')}</Typography>
                                    <Typography
                                        className="price"
                                        variant="body2"
                                    >
                                        {largestDimensions.short}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider />

                            <TbxShadowScroll
                                scrollContainerSx={classes.partsList}
                                wrapperSx={classes.partsListContainer}
                            >
                                {!isLoadingItems
                                    ? orderItems.map((item) => (
                                          <Box
                                              className="part"
                                              key={item.id}
                                              sx={{
                                                  alignItems: 'end',
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                              }}
                                          >
                                              <Box
                                                  sx={[
                                                      {
                                                          display: 'flex',
                                                          flexDirection: 'column',
                                                      },
                                                      classes.partInfo,
                                                  ]}
                                              >
                                                  <Typography
                                                      sx={classes.strongText}
                                                      variant="body1"
                                                  >
                                                      {item.name}
                                                  </Typography>

                                                  <Box
                                                      sx={[
                                                          {
                                                              display: 'flex',
                                                              flexDirection: 'row',
                                                          },
                                                          classes.partDetailsInfo,
                                                      ]}
                                                  >
                                                      <Typography
                                                          sx={classes.smallText}
                                                          variant="body2"
                                                      >
                                                          {t('Material')}: {orderItemMaterial(item)?.materialName}
                                                      </Typography>
                                                      <Typography
                                                          sx={classes.smallText}
                                                          variant="body2"
                                                      >
                                                          {t('Thickness')}: {item.thickness}
                                                          {lengthUnit}
                                                      </Typography>
                                                      <Typography
                                                          sx={classes.smallText}
                                                          variant="body2"
                                                      >
                                                          {t('Quantity')}: {item.quantity}
                                                      </Typography>
                                                  </Box>
                                              </Box>

                                              <Typography
                                                  sx={classes.strongText}
                                                  variant="body1"
                                              >
                                                  {formatCurrency(
                                                      item.linePrice,
                                                      storeSettings?.currencyCode,
                                                      storeSettings?.locale
                                                  )}
                                              </Typography>
                                          </Box>
                                      ))
                                    : []}
                            </TbxShadowScroll>

                            <Box
                                sx={[
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                    classes.totalInfo,
                                ]}
                            >
                                {order.minimumQuoteChargePrice ? (
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="body1">{t('Minimum order charge')}</Typography>
                                        <Typography
                                            sx={classes.strongText}
                                            variant="body1"
                                        >
                                            {formatCurrency(
                                                orderMinimumChargePrice,
                                                storeSettings?.currencyCode,
                                                storeSettings?.locale
                                            )}
                                        </Typography>
                                    </Box>
                                ) : null}

                                {order.deliveryPrice ? (
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="body1">{t('Delivery')}</Typography>
                                        <Typography
                                            sx={classes.strongText}
                                            variant="body1"
                                        >
                                            {formatCurrency(
                                                deliveryPrice,
                                                storeSettings?.currencyCode,
                                                storeSettings?.locale
                                            )}
                                        </Typography>
                                    </Box>
                                ) : null}

                                <Divider />

                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body1">{t('Subtotal')}</Typography>
                                    <Typography
                                        sx={classes.strongText}
                                        variant="body1"
                                    >
                                        {formatCurrency(
                                            subTotalWithDiff,
                                            storeSettings?.currencyCode,
                                            storeSettings?.locale
                                        )}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body1">
                                        {t('Taxes')} ({fractionToPercentage(orderTaxRate)}%)
                                    </Typography>
                                    <Typography
                                        sx={classes.strongText}
                                        variant="body1"
                                    >
                                        {formatCurrency(totalTax, storeSettings?.currencyCode, storeSettings?.locale)}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="h6">{t('Total')}</Typography>
                                    <Typography
                                        sx={classes.strongText}
                                        variant="h6"
                                    >
                                        {formatCurrency(total, storeSettings?.currencyCode, storeSettings?.locale)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </>
        )
    }
}

export default memo(PaymentSuccess)
